import React from 'react';
import { useTranslation } from 'react-i18next';
import PanelFrame from '@components-clean/core/panel-frame';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { PanelTheme, PanelType } from 'types/graphql-global-types';
import { RadialChartKris, RadialChartObsActions } from '@utils/constants';
import { useGetRadialChartStudyData } from '@apis/bff-api/central-monitoring';

const ChartPanel = ({
    studyRef,
    datasetId,
    allowKRIReviewsState,
}: {
    studyRef: string | undefined;
    datasetId: number | undefined;
    allowKRIReviewsState: boolean;
}) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'monitoringToolkit.radialChart',
    });

    const { data: radialChartData, error, isLoading } = useGetRadialChartStudyData(studyRef, datasetId);

    const panelDataSeries: { name: string; type: string; data: number[] }[] = [];
    const xCategories = allowKRIReviewsState
        ? [
              t('siteObservations'),
              t('siteActions'),
              t('siteReviewLogs'),
              t('studyObservations'),
              t('studyActions'),
              t('studyReviewLogs'),
              t('kriReviews'),
          ]
        : [
              t('siteObservations'),
              t('siteActions'),
              t('siteReviewLogs'),
              t('studyObservations'),
              t('studyActions'),
              t('studyReviewLogs'),
          ];

    const siteObservationsData = radialChartData?.siteObservations;
    const siteActionsData = radialChartData?.siteActions;
    const siteReviewLogData = radialChartData?.siteReviewLogs;
    const studyObservationsData = radialChartData?.studyObservations;
    const studyActionsData = radialChartData?.studyActions;
    const studyReviewLogsData = radialChartData?.studyReviewLogs;
    const kriReviewsData = radialChartData?.kriReviews;
    const { legendToggleValue } = usePageToolbar();

    siteObservationsData?.forEach((siteObservation) => {
        if (siteObservation.count === 0) return;
        panelDataSeries.push({
            name: siteObservation.openClosed === 0 ? RadialChartObsActions.Open : RadialChartObsActions.Closed,
            type: 'column',
            data: !allowKRIReviewsState
                ? [siteObservation.count, 0, 0, 0, 0, 0]
                : [siteObservation.count, 0, 0, 0, 0, 0, 0],
        });
    });

    siteActionsData?.forEach((siteAction) => {
        if (siteAction.count === 0) return;
        panelDataSeries.push({
            name: siteAction.openClosed === 0 ? RadialChartObsActions.Open : RadialChartObsActions.Closed,
            type: 'column',
            data: !allowKRIReviewsState ? [0, siteAction.count, 0, 0, 0, 0] : [0, siteAction.count, 0, 0, 0, 0, 0],
        });
    });

    siteReviewLogData?.forEach((siteReviewLog) => {
        if (siteReviewLog.count === 0) return;
        panelDataSeries.push({
            name: siteReviewLog.title,
            type: 'column',
            data: !allowKRIReviewsState
                ? [0, 0, siteReviewLog.count, 0, 0, 0]
                : [0, 0, siteReviewLog.count, 0, 0, 0, 0],
        });
    });

    studyObservationsData?.forEach((studyObservation) => {
        if (studyObservation.count === 0) return;
        panelDataSeries.push({
            name: studyObservation.openClosed === 0 ? RadialChartObsActions.Open : RadialChartObsActions.Closed,
            type: 'column',
            data: !allowKRIReviewsState
                ? [0, 0, 0, studyObservation.count, 0, 0]
                : [0, 0, 0, studyObservation.count, 0, 0, 0],
        });
    });

    studyActionsData?.forEach((studyAction) => {
        if (studyAction.count === 0) return;
        panelDataSeries.push({
            name: studyAction.openClosed === 0 ? RadialChartObsActions.Open : RadialChartObsActions.Closed,
            type: 'column',
            data: !allowKRIReviewsState ? [0, 0, 0, 0, studyAction.count, 0] : [0, 0, 0, 0, studyAction.count, 0, 0],
        });
    });

    studyReviewLogsData?.forEach((studyReviewLog) => {
        if (studyReviewLog.count === 0) return;
        panelDataSeries.push({
            name: studyReviewLog.title,
            type: 'column',
            data: !allowKRIReviewsState
                ? [0, 0, 0, 0, 0, studyReviewLog.count]
                : [0, 0, 0, 0, 0, studyReviewLog.count, 0],
        });
    });

    if (allowKRIReviewsState) {
        kriReviewsData?.forEach((kriReview) => {
            if (kriReview.count === 0) return;
            panelDataSeries.push({
                name: kriReview.reviewedUnreviewed === 0 ? RadialChartKris.Reviewed : RadialChartKris.Unreviewed,
                type: 'column',
                data: [0, 0, 0, 0, 0, 0, kriReview.count],
            });
        });
    }

    if (error) {
        return (
            <PanelFrame
                title={t('title')}
                data-testid="monitoring-status-card"
                className="monitoring-status-card"
                panelType={PanelType.GRAPH}
                graphType="RADIAL"
                panelData={{
                    series: [],
                    xCategories: allowKRIReviewsState
                        ? [
                              t('siteObservations'),
                              t('siteActions'),
                              t('siteReviewLogs'),
                              t('studyObservations'),
                              t('studyActions'),
                              t('studyReviewLogs'),
                              t('kriReviews'),
                          ]
                        : [
                              t('siteObservations'),
                              t('siteActions'),
                              t('siteReviewLogs'),
                              t('studyObservations'),
                              t('studyActions'),
                              t('studyReviewLogs'),
                          ],
                }}
                hidePanelSetup
                theme={PanelTheme.THEME_2}
            />
        );
    }

    return (
        <PanelFrame
            title={t('title')}
            data-testid="monitoring-status-card"
            className="monitoring-status-card"
            panelType={PanelType.GRAPH}
            graphType="RADIAL"
            panelData={{
                series: panelDataSeries,
                xCategories,
            }}
            hidePanelSetup
            theme={PanelTheme.THEME_2}
            isLoading={isLoading}
            showLegend={legendToggleValue?.current}
        />
    );
};

export default ChartPanel;
